import React, { useState, useMemo } from 'react';
import {
    MapPin, Star, Clock, Phone, Globe, Navigation,
    ChevronDown, ChevronUp, Utensils, Coffee, ShoppingBag,
    Landmark, Dumbbell, Building, TreePine, Heart,
    Car, Accessibility, Lightbulb, ExternalLink, BadgeCheck
} from 'lucide-react';
import { Button } from './ui/button';

const TYPE_ICONS = {
    restaurant: Utensils, cafe: Coffee, bar: Coffee,
    hotel: Building, museum: Landmark, park: TreePine,
    store: ShoppingBag, gym: Dumbbell, hospital: Heart,
    attraction: Landmark,
};

const PlaceView = ({ place, url, hideCoverPhoto = false }) => {
    const [hoursOpen, setHoursOpen] = useState(false);

    const TypeIcon = TYPE_ICONS[place?.place_type] || MapPin;

    const today = useMemo(() => {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[new Date().getDay()];
    }, []);

    const todayHours = useMemo(() => {
        if (!place?.opening_hours?.length) return null;
        return place.opening_hours.find(h => h.day === today);
    }, [place, today]);

    if (!place) return null;

    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place.address || place.place_name)}`;

    return (
        <div className="space-y-4" data-testid="place-view">

            {/* Cover Photo */}
            {place.photo_url && !hideCoverPhoto && (
                <div className="rounded-xl overflow-hidden border border-border/60 -mx-1" data-testid="place-cover-photo">
                    <img
                        src={place.photo_url}
                        alt={place.place_name}
                        className="w-full h-48 object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            )}

            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                     style={{ background: '#f0fdf4' }}>
                    <TypeIcon className="w-5 h-5" style={{ color: '#16a34a' }} />
                </div>
                <div className="min-w-0 flex-1">
                    <h2 className="font-heading text-xl font-bold tracking-tight leading-snug" data-testid="place-name">
                        {place.place_name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        {place.categories?.map((cat) => (
                            <span key={cat} className="text-xs text-muted-foreground">{cat}</span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Rating + Price + Reviews */}
            {(place.rating || place.price_level) && (
                <div className="flex items-center gap-4 flex-wrap">
                    {place.rating && (
                        <div className="flex items-center gap-1.5">
                            <div className="flex items-center gap-0.5 px-2 py-1 rounded-lg" style={{ background: '#fef9c3' }}>
                                <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                                <span className="text-sm font-bold text-amber-700">{place.rating}</span>
                            </div>
                            {place.review_count && (
                                <span className="text-xs text-muted-foreground">
                                    ({place.review_count.toLocaleString()} reviews)
                                </span>
                            )}
                            {place.source === 'google_places_api' && (
                                <span className="flex items-center gap-0.5 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full" data-testid="google-verified-badge">
                                    <BadgeCheck className="w-3 h-3" />
                                    Google
                                </span>
                            )}
                        </div>
                    )}
                    {place.price_level && (
                        <span className="text-sm font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                            {place.price_level}
                        </span>
                    )}
                </div>
            )}

            {/* Address */}
            {place.address && (
                <div className="flex items-start gap-2 text-sm" data-testid="place-address">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{place.address}</span>
                </div>
            )}

            {/* AI Description */}
            {place.description && (
                <div className="bg-gradient-to-br from-emerald-50/60 to-teal-50/40 rounded-xl p-4 border border-emerald-100/60">
                    <p className="text-sm text-foreground leading-relaxed" data-testid="place-description">
                        {place.description}
                    </p>
                </div>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-3 gap-2" data-testid="place-actions">
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
                   className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors">
                    <Navigation className="w-4 h-4 text-blue-600" />
                    <span className="text-[11px] font-medium text-blue-700">Directions</span>
                </a>
                {place.phone ? (
                    <a href={`tel:${place.phone}`}
                       className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                        <Phone className="w-4 h-4 text-green-600" />
                        <span className="text-[11px] font-medium text-green-700">Call</span>
                    </a>
                ) : (
                    <div className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-gray-50 opacity-40">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-[11px] font-medium text-gray-400">Call</span>
                    </div>
                )}
                {place.website ? (
                    <a href={place.website} target="_blank" rel="noopener noreferrer"
                       className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                        <Globe className="w-4 h-4 text-purple-600" />
                        <span className="text-[11px] font-medium text-purple-700">Website</span>
                    </a>
                ) : (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                       className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-purple-50 hover:bg-purple-100 transition-colors">
                        <ExternalLink className="w-4 h-4 text-purple-600" />
                        <span className="text-[11px] font-medium text-purple-700">Maps</span>
                    </a>
                )}
            </div>

            {/* Opening Hours */}
            {place.opening_hours?.length > 0 && (
                <div className="bg-white border border-border/60 rounded-xl overflow-hidden" data-testid="place-hours">
                    <button
                        onClick={() => setHoursOpen(!hoursOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Opening Hours</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {todayHours && (
                                <span className="text-xs text-muted-foreground">
                                    Today: {todayHours.hours}
                                </span>
                            )}
                            {hoursOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                    </button>
                    {hoursOpen && (
                        <div className="px-4 pb-3 space-y-1.5">
                            {place.opening_hours.map((h) => (
                                <div key={h.day} className={`flex justify-between text-sm py-1 px-2 rounded ${h.day === today ? 'bg-primary/5 font-medium' : ''}`}>
                                    <span className={h.day === today ? 'text-primary' : 'text-muted-foreground'}>{h.day}</span>
                                    <span className={h.day === today ? 'text-primary' : 'text-foreground'}>{h.hours}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Highlights / Features */}
            {place.highlights?.length > 0 && (
                <div data-testid="place-highlights">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Features</h4>
                    <div className="flex flex-wrap gap-1.5">
                        {place.highlights.map((h) => (
                            <span key={h} className="text-xs bg-secondary/70 text-foreground px-2.5 py-1 rounded-full">{h}</span>
                        ))}
                    </div>
                </div>
            )}

            {/* Popular Dishes */}
            {place.popular_dishes?.length > 0 && (
                <div data-testid="place-dishes">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Popular Items</h4>
                    <div className="flex flex-wrap gap-1.5">
                        {place.popular_dishes.map((d) => (
                            <span key={d} className="text-xs bg-amber-50 text-amber-800 px-2.5 py-1 rounded-full border border-amber-200/60">
                                {d}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Tips */}
            {place.tips?.length > 0 && (
                <div data-testid="place-tips">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Tips</h4>
                    <div className="space-y-2">
                        {place.tips.map((tip, i) => (
                            <div key={`tip-${tip.slice(0, 20)}`} className="flex items-start gap-2 text-sm">
                                <Lightbulb className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <span className="text-foreground">{tip}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Accessibility & Parking */}
            {(place.accessibility?.length > 0 || place.parking) && (
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {place.accessibility?.map((a) => (
                        <div key={a} className="flex items-center gap-1">
                            <Accessibility className="w-3 h-3" />
                            <span>{a}</span>
                        </div>
                    ))}
                    {place.parking && (
                        <div className="flex items-center gap-1">
                            <Car className="w-3 h-3" />
                            <span>{place.parking}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Embedded Map */}
            <div className="rounded-xl overflow-hidden border border-border/60" data-testid="place-map">
                <iframe
                    title="Map"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(place.address || place.place_name)}&output=embed`}
                />
            </div>

            {/* Open in Maps button */}
            <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(url || directionsUrl, '_blank')}
                data-testid="open-in-maps-btn"
            >
                <MapPin className="w-4 h-4 mr-2" />
                Open in Google Maps
            </Button>
        </div>
    );
};

export default PlaceView;
